import { DataStore } from "@ezhou/core/lib/model/DataStore";

import { UserProfileSliceKey } from "../model/appConstants";
import { getUserProfileFromCache } from "./userSettingService";

export const LicenseExpirationWarningDays = 15;

export type LicenseStatus = "ok" | "expiringSoon" | "expired" | "notFound";

export type LicenseState = {
  status: LicenseStatus;
  organizationName?: string;
  expirationDate?: Date;
  daysRemaining?: number;
};

const MillisecondsPerDay = 24 * 60 * 60 * 1000;

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * Determines the license state of the user's current working organization based on
 * its LicenseExpirationDate. Returns "notFound" when there is no profile, no current
 * organization, or no (valid) license expiration date configured.
 */
export const getCurrentOrganizationLicenseState = (dataStore: DataStore): LicenseState => {
  if (!dataStore.isSliceAvailable(UserProfileSliceKey)) {
    return { status: "notFound" };
  }

  let organization;
  try {
    organization = getUserProfileFromCache(dataStore)?.defaultOrganizationInfo;
  } catch {
    return { status: "notFound" };
  }

  if (!organization || !organization.licenseExpirationDate) {
    return { status: "notFound", organizationName: organization?.fullName };
  }

  const expirationDate = new Date(organization.licenseExpirationDate);
  if (isNaN(expirationDate.getTime())) {
    return { status: "notFound", organizationName: organization.fullName };
  }

  const daysRemaining = Math.round(
    (startOfDay(expirationDate).getTime() - startOfDay(new Date()).getTime()) / MillisecondsPerDay,
  );

  if (daysRemaining < 0) {
    return { status: "expired", organizationName: organization.fullName, expirationDate, daysRemaining };
  }
  if (daysRemaining <= LicenseExpirationWarningDays) {
    return { status: "expiringSoon", organizationName: organization.fullName, expirationDate, daysRemaining };
  }
  return { status: "ok", organizationName: organization.fullName, expirationDate, daysRemaining };
};
